import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section id="about" data-testid="about-section" ref={ref} className="relative py-28 md:py-40 border-t border-treeline">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-10 md:gap-16 items-start">
        <div className="col-span-12 md:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-lime mb-8"
          >
            [ 01 ] · About me
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-cream"
          >
            I turn complex, high-volume data into{" "}
            <span className="italic text-lime">decisions people act on.</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
            className="mt-10 space-y-6 font-mono text-sm md:text-base leading-relaxed text-sage max-w-xl"
            data-testid="about-bio"
          >
            <p>
              One of the things I've come to appreciate most is how transferable data skills are. You can find
              yourself in an industry or problem space you never expected and still use data to understand what's
              happening and improve how things are done.
            </p>
            <p>
              My academic and professional experiences have exposed me to very different types of work, and while
              the problems themselves may vary, data has consistently been the tool that connects them. That
              contrast has made me even more interested in exploring how data can be applied across different
              industries. I enjoy learning unfamiliar domains, finding patterns, and turning information into
              something people can use.
            </p>
            <p className="pt-4 border-t border-treeline text-[11px] uppercase tracking-[0.18em] text-sage/80 leading-loose" data-testid="about-toolbox">
              Python · SQL · PostgreSQL · R · JavaScript · Pandas · NumPy · Excel · Power BI · Tableau ·{" "}
              <span className="text-lime/90">Google Analytics</span> · <span className="text-lime/90">Google Ads</span> ·{" "}
              <span className="text-lime/90">Git</span> · AWS · Scikit-learn · TensorFlow · FAISS · LLMs · Elasticsearch · Streamlit
            </p>
          </motion.div>
        </div>

        <div className="col-span-12 md:col-span-5 md:pt-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-lime/25 translate-x-4 translate-y-4 pointer-events-none" />
            <div className="relative overflow-hidden border border-treeline">
              <motion.div style={{ y: yImg }}>
                <img
                  src="/assets/headshot.png"
                  alt="Sarron A. Tadesse headshot"
                  data-testid="about-headshot"
                  className="w-full aspect-[3/4] object-cover object-top scale-[1.1] [transform-origin:top_center]"
                />
              </motion.div>
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(204,255,0,0.10),transparent_60%)] pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-forest/80 to-transparent pointer-events-none" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
